// 'use client';  // uncomment if this is a Next.js client component

import React, { useEffect, useState } from 'react';
import styles from './styles/LearnersSummary.module.css';

// Helper: compare two dates by local calendar day
function isSameLocalDate(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// 🔹 Central helper: always get the real section object + meetings array
function getSectionCore(rawSection) {
  // Sometimes you get { section: {...} }, sometimes the section itself
  const core = rawSection.section || rawSection || {};
  const meetings = core.meetings || core.Meetings || [];
  return { core, meetings: Array.isArray(meetings) ? meetings : [] };
}

// Helper: does this section have at least one meeting happening today?
function sectionMeetsToday(rawSection, today) {
  const { meetings } = getSectionCore(rawSection);
  if (!meetings.length) return false;

  return meetings.some((mtg) => {
    const rawDate =
      mtg.startTime ||
      mtg.startDate ||
      mtg.date ||
      mtg.start_time ||
      null;

    if (!rawDate) return false;
    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return false;

    return isSameLocalDate(d, today);
  });
}

// Get the next upcoming meeting (any future date/time)
function getNextStartTime(rawSection, now) {
  const { meetings } = getSectionCore(rawSection);
  if (!meetings.length) return null;

  let best = null;

  for (const mtg of meetings) {
    const rawStart =
      mtg.startTime ||
      mtg.startDate ||
      mtg.date ||
      mtg.start_time ||
      null;

    if (!rawStart) continue;
    const start = new Date(rawStart);
    if (Number.isNaN(start.getTime())) continue;

    // Only future meetings
    if (start <= now) continue;

    if (!best || start < best) {
      best = start;
    }
  }

  return best;
}

// "Starts in" logic
function getDiffParts(futureDate, now) {
  const nowMs = now.getTime();
  const targetMs = futureDate.getTime();
  let diff = Math.max(0, targetMs - nowMs);

  const days = Math.floor(diff / (24 * 3600e3));
  diff -= days * 24 * 3600e3;

  const hours = Math.floor(diff / 3600e3);
  diff -= hours * 3600e3;

  const minutes = Math.floor(diff / 60e3);

  return { days, hours, minutes };
}

export default function LearnersOverview({ learners, router }) {
  // Keep a "live" now that updates every minute
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Update every 60 seconds; you can change to 30 * 1000 if you want finer steps
    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  const today = now;

  return (
    <div className={styles.learnersSummary}>
      <p className={styles.stepText}>
        Showing only classes that meet today. To view all classes for any learner, click their
        name in the list on the right.
      </p>

      <ul className={styles.learnersSummaryList}>
        {learners.map((learner) => {
          const sectionsRaw =
            learner.enrolledSections ||
            learner.sections ||
            learner.learnerSections ||
            [];

          const sections = Array.isArray(sectionsRaw) ? sectionsRaw : [];

          // Sections that have at least one meeting today
          const todaySections = sections.filter((sec) =>
            sectionMeetsToday(sec, today)
          );

          const courseNames = todaySections
            .map((sec) => {
              const { core } = getSectionCore(sec);
              const course =
                core.course || core.courseInfo || null;

              return (
                course?.title ||
                course?.name ||
                core.courseTitle ||
                core.courseName ||
                null
              );
            })
            .filter(Boolean);

          const uniqueCourseNames = Array.from(new Set(courseNames));

          const baseLabel =
            todaySections.length > 0 && uniqueCourseNames.length > 0
              ? uniqueCourseNames.join(', ')
              : 'No classes meeting today';

          // Find the earliest upcoming meeting across *all* sections
          let nextStart = null;
          sections.forEach((sec) => {
            const candidate = getNextStartTime(sec, now);
            if (candidate && (!nextStart || candidate < nextStart)) {
              nextStart = candidate;
            }
          });

          let startsInLabel = null;
          if (nextStart) {
            const { days, hours, minutes } = getDiffParts(nextStart, now);
            // Build full-word "Starts in" label
            let parts = [];

            if (days) {
              parts.push(days === 1 ? '1 day' : `${days} days`);
            }

            if (hours) {
              parts.push(hours === 1 ? '1 hour' : `${hours} hours`);
            }

            // Always show minutes
            parts.push(minutes === 1 ? '1 minute' : `${minutes} minutes`);

            startsInLabel = `Starts in ${parts.join(' ')}`;
          }

          const displayLabel = startsInLabel
            ? `${baseLabel} • ${startsInLabel}`
            : baseLabel;

          return (
            <li
              key={learner.id || learner.name}
              className={styles.learnersSummaryItem}
              onClick={() => router.push(`/student-page/${learner.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.learnersSummaryNameRow}>
                <span className={styles.learnersSummaryName}>
                  {learner.name || learner.firstName || 'Learner'}
                </span>
              </div>

              <div className={styles.learnersSummaryClassesRow}>
                <span className={styles.learnersSummaryClasses}>
                  {displayLabel}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}